import { ObjectId } from "mongodb";
import mongoose, { Schema, Document, Model } from "mongoose";

export interface RemovalInfo {
  reason:
    | "USER_REQUESTED"
    | "ADMIN_INITIATED"
    | "TERMS_VIOLATION"
    | "AUTOMATED_SYSTEM"
    | "OTHER";
  removedAt: Date;
  internalNotes?: string; // Optional: for internal comments
}

export interface ConversionDocument extends Document {
  userid: string;
  modelIds: ObjectId[];
  jobid?: string;
  originalUrl: string;
  convertedUrl: string; // vocals
  instrumentalUrl?: string;
  status?: string;
  progress?: number;
  conversionType?: string;
  shortid: string;
  name?: string;
  downloads: number;
  coverType?: string;
  ip?: string;
  mode?: string;
  failCount: number;
  toolMode: {
    isToolMode: boolean;
    type?: string;
  };
  deleted: boolean;
  type: string;
  isFree: boolean;
  schemaVersion?: number;
  createdAt: Date;
  updatedAt: Date;
  outputUrls?: {
    audio?: string;
    video?: string;
    transcription?: string;
  };
  voiceTimes: [];
  userPlanName?: string;
  duration: number;
  clip?: {
    inTime?: number;
    outTime?: number;
    duration?: number;
  };
  outputFileIds?: ObjectId[];
  inputFileIds?: ObjectId[];
  coverSettings?: {
    addEffects?: boolean;
    extractBackground?: boolean;
    pitch?: any[];
    highQuality?: boolean;
    levelDb?: number;
    skipExtraction?: boolean;
    swapRate?: number;
    autotune?: {
      enabled?: boolean;
      strength?: number;
    };
    tts?: any;
    clip?: {
      inTime?: number;
      outTime?: number;
      duration?: number;
    };
  };
  watermark?: {
    payload?: string;
    version?: string;
  };
  metadata?: any;
  ipInfo?: {
    country: {
      code: string;
      name: string;
    };
    city: {
      name: string;
    };
    region: {
      code: string;
      name: string;
    };
    address: string;
  };
  removal?: RemovalInfo;
}

const ConversionSchema = new Schema<ConversionDocument>(
  {
    userid: {
      type: String,
      required: true,
    },
    modelIds: [{ type: mongoose.Schema.Types.ObjectId, ref: "Model" }],
    voiceTimes: [],
    jobid: {
      type: String,
    },
    originalUrl: {
      type: String,
      required: true,
    },
    convertedUrl: {
      type: String,
    },

    instrumentalUrl: {
      type: String,
    },
    status: {
      type: String,
    },
    progress: {
      type: Number,
      default: 0,
    },
    outputFileIds: [{ type: mongoose.Schema.Types.ObjectId, ref: "File" }],
    inputFileIds: [{ type: mongoose.Schema.Types.ObjectId, ref: "File" }],
    conversionType: {
      type: String,
    },
    coverType: {
      type: String,
    },

    outputUrls: {
      audio: {
        type: String,
      },
      video: {
        type: String,
      },
      transcription: {
        type: String,
      },
    },
    shortid: {
      type: String,
      unique: true,
    },
    name: {
      type: String,
    },
    downloads: {
      type: Number,
      default: 0,
    },

    ip: {
      type: String,
      required: false,
    },
    type: {
      type: String,
    },
    mode: {
      type: String,
    },
    failCount: {
      type: Number,
      default: 0,
    },
    toolMode: {
      isToolMode: {
        type: Boolean,
        default: false,
      },
      type: {
        type: String,
      },
    },
    deleted: {
      type: Boolean,
      default: false,
    },
    ipInfo: {
      country: {
        code: {
          type: String,
        },
        name: {
          type: String,
        },
      },
      city: {
        name: {
          type: String,
        },
      },
      region: {
        code: {
          type: String,
        },
        name: {
          type: String,
        },
      },
      address: {
        type: String,
      },
    },
    removal: {
      type: new mongoose.Schema(
        {
          reason: {
            type: String,
            required: true,
            enum: [
              "USER_REQUESTED",
              "DMCA_TAKEDOWN",
              "TERMS_VIOLATION",
              "AUTOMATED_SYSTEM",
              "OTHER",
            ],
          },
          removedAt: {
            type: Date,
            default: Date.now,
            required: true,
          },
          internalNotes: {
            type: String,
            required: false,
          },
        },
        { _id: false },
      ),
      required: false,
      index: true,
    },
    isFree: {
      type: Boolean,
      default: false,
    },
    schemaVersion: {
      type: Number,
      default: 1,
    },
    userPlanName: {
      type: String,
      required: false,
    },
    duration: {
      type: Number,
      required: false,
    },
    clip: {
      duration: {
        type: Number,
      },
      inTime: {
        type: Number,
      },
      outTime: {
        type: Number,
      },
    },

    coverSettings: {
      addEffects: {
        type: Boolean,
      },
      tts: {
        type: Object,
        required: false,
      },
      extractBackground: {
        type: Boolean,
      },
      pitch: {
        type: Array,
      },
      highQuality: {
        type: Boolean,
      },
      levelDb: {
        type: Number,
      },
      skipExtraction: {
        type: Boolean,
      },
      swapRate: {
        type: Number,
      },
      autotune: {
        enabled: {
          type: Boolean,
        },
        strength: {
          type: Number,
        },
      },
    },
    metadata: {
      type: Object,
    },
    watermark: {
      payload: {
        type: String,
        required: false,
      },
      version: {
        type: String,
        required: false,
      },
    },
  },
  { timestamps: true },
);

ConversionSchema.index({ createdAt: 1 });
ConversionSchema.index({ modelid: 1 });

const ConversionModel: Model<ConversionDocument> =
  mongoose?.models?.Conversion ||
  mongoose.model<ConversionDocument>("Conversion", ConversionSchema);

export default ConversionModel;
